import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";

import ContextConfigFormNameField from "components/ContextConfigForm/NameField";
import FormDescriptionField from "components/Environment/components/FormItems/DescriptionField";
import FormIsSecretField from "components/Environment/components/FormItems/IsSecretField";
import FormValueField from "components/Environment/components/FormItems/ValueField";
import { useObserveForWarning } from "components/WarningContext/useObserveForWarning";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Card from "ds/components/Card";

import { ContextAttachedVariable } from "../types";

type ContextVariablesAddFormProps = {
  onSave: (item: ContextAttachedVariable) => void;
};

const ContextVariablesAddForm = ({ onSave }: ContextVariablesAddFormProps) => {
  const builderForm = useForm<ContextAttachedVariable>({
    defaultValues: {
      variableName: "",
      value: "",
      isSecret: true,
      description: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
    watch,
  } = builderForm;

  const variableName = watch("variableName");
  const value = watch("value");

  useObserveForWarning(
    !!(variableName && value),
    <>
      You have not added the <strong>variable</strong>. Do you want to continue without adding it?
    </>
  );

  const onSubmit = useCallback(
    () =>
      handleSubmit((formData: ContextAttachedVariable) => {
        onSave(formData);
        reset();
      })(),
    [onSave, reset, handleSubmit]
  );

  return (
    <Card variant="secondary">
      <FormProvider {...builderForm}>
        <Box gap="medium" fullWidth margin="0 0 large">
          <ContextConfigFormNameField name="variableName" />
          <FormValueField name="value" isSecret={watch("isSecret")} />
        </Box>
        <FormIsSecretField name="isSecret" />
        <FormDescriptionField name="description" />
        <Box justify="end" fullWidth margin="large 0 0">
          <Button variant="contrast" type="submit" onPress={onSubmit} disabled={!isValid}>
            Add variable
          </Button>
        </Box>
      </FormProvider>
    </Card>
  );
};

export default ContextVariablesAddForm;
